import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Package, User, CreditCard, Calendar, Clock, CheckCircle, XCircle, Truck, CheckSquare, Save, Zap, CheckCircle2, AlertTriangle, Mail, Send } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { toast } from 'react-toastify';
import { getImageUrl, generateSlug } from '../../utils/helpers';
import { ORDER_STATUS } from '../../utils/constants';

const AdminOrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const [shippingFee, setShippingFee] = useState('');
    const [initialShippingFee, setInitialShippingFee] = useState('');
    
    const [orderStatus, setOrderStatus] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('false');

    // State cho thông tin giao hàng
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    
    const [isSaving, setIsSaving] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const orderData = await adminStaffService.getOrderDetail(id);
                setOrder(orderData);
                
                setOrderStatus(orderData.orderStatus.toString());
                setPaymentStatus(orderData.isPaid ? 'true' : 'false');
                
                const fee = orderData.orderShippingFee !== null ? orderData.orderShippingFee : '';
                setShippingFee(fee);
                setInitialShippingFee(fee);

                // Gán dữ liệu khách hàng vào state
                setCustomerName(orderData.orderCustomerName || '');
                setCustomerPhone(orderData.orderPhone || '');
                setCustomerEmail(orderData.orderEmail || '');
                setCustomerAddress(orderData.orderAddress || '');

            } catch (error) {
                console.error("Lỗi:", error);
                toast.error("Không thể tải dữ liệu đơn hàng!");
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [id]);

    const initialOrderStatus = order?.orderStatus?.toString() || '';
    const initialPaymentStatus = order?.isPaid ? 'true' : 'false';

    const isShippingFeeChanged = String(shippingFee) !== String(initialShippingFee);
    const isStatusChanged = orderStatus !== initialOrderStatus;
    const isPaymentChanged = paymentStatus !== initialPaymentStatus;

    // Kiểm tra xem thông tin khách hàng có bị thay đổi không
    const isCustomerInfoChanged = 
        customerName !== (order?.orderCustomerName || '') ||
        customerPhone !== (order?.orderPhone || '') ||
        customerEmail !== (order?.orderEmail || '') ||
        customerAddress !== (order?.orderAddress || '');

    const hasUnsavedChanges = isShippingFeeChanged || isStatusChanged || isPaymentChanged || isCustomerInfoChanged;

    const handleUpdateOrder = async () => {
        setIsSaving(true);
        try {
            let hasChanged = false;
            const currentStatus = order.orderStatus; 
            const newStatus = parseInt(orderStatus); 
            
            const currentIsPaid = order.isPaid || false;
            const newIsPaid = paymentStatus === 'true';

            // 1. Cập nhật trạng thái thanh toán
            if (currentIsPaid !== newIsPaid) {
                await adminStaffService.updateOrderPaymentStatus(id, newIsPaid);
                hasChanged = true;
            }

            // 2. Cập nhật phí vận chuyển
            if (isShippingFeeChanged) {
                const finalFee = (shippingFee === '' || isNaN(shippingFee)) ? 0 : Number(shippingFee);
                await adminStaffService.confirmOrderWithShipping(id, null, finalFee);
                hasChanged = true;
            }

            // 3. Cập nhật thông tin giao hàng
            if (isCustomerInfoChanged) {
                await adminStaffService.updateOrderDeliveryInfo(id, {
                    receiverName: customerName,
                    receiverPhone: customerPhone,
                    receiverEmail: customerEmail,
                    fullAddress: customerAddress
                });
                hasChanged = true;
            }

            // 4. Cập nhật trạng thái đơn hàng
            if (currentStatus !== newStatus) {
                await adminStaffService.updateOrderStatus(id, newStatus);
                hasChanged = true;
            }

            if (hasChanged) {
                toast.success("Cập nhật đơn hàng thành công!");
                const newData = await adminStaffService.getOrderDetail(id);
                setOrder(newData);
                setOrderStatus(newData.orderStatus.toString());
                setPaymentStatus(newData.isPaid ? 'true' : 'false');
                
                const fee = newData.orderShippingFee !== null ? newData.orderShippingFee : '';
                setShippingFee(fee);
                setInitialShippingFee(fee);

                setCustomerName(newData.orderCustomerName || '');
                setCustomerPhone(newData.orderPhone || '');
                setCustomerEmail(newData.orderEmail || '');
                setCustomerAddress(newData.orderAddress || '');
            } else {
                toast.info("Không có thay đổi nào để lưu.");
            }
            
        } catch (error) {
            console.error("Lỗi hệ thống:", error);
            const errMsg = error.response?.data || error.message || "Đã xảy ra lỗi khi cập nhật!";
            toast.error("Cập nhật thất bại: " + errMsg);
        } finally {
            setIsSaving(false);
        }
    };

    const actualEmail = order?.account?.email || order?.orderEmail;

    const handleSendStatusEmail = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!actualEmail) {
            toast.warning("Không thể gửi email: Đơn hàng này không có địa chỉ email!");
            return;
        }

        if (!emailRegex.test(actualEmail)) {
            toast.warning(`Không thể gửi email: Địa chỉ "${actualEmail}" không hợp lệ!`);
            return;
        }

        setIsSendingEmail(true);
        try {
            await adminStaffService.sendOrderStatusEmail(id);
            toast.success("Đã gửi email thông báo trạng thái mới nhất cho khách hàng!");
            const newData = await adminStaffService.getOrderDetail(id);
            setOrder(newData);
        } catch (error) {
            toast.error("Có lỗi xảy ra từ máy chủ khi gửi email!");
        } finally {
            setIsSendingEmail(false);
        }
    };

    const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price || 0);

    const getStatusBadge = (status) => {
        switch(Number(status)) {
            case ORDER_STATUS.PENDING: return <span className="flex items-center gap-1.5 text-gray-700 bg-gray-100 px-3 py-1.5 rounded-full text-sm font-bold"><Clock size={16}/> Chờ xác nhận</span>;
            case ORDER_STATUS.CONFIRMED: return <span className="flex items-center gap-1.5 text-yellow-700 bg-yellow-100 px-3 py-1.5 rounded-full text-sm font-bold"><CheckSquare size={16}/> Đã xác nhận</span>;
            case ORDER_STATUS.IN_WAREHOUSE: return <span className="flex items-center gap-1.5 text-purple-700 bg-purple-100 px-3 py-1.5 rounded-full text-sm font-bold"><Package size={16}/> Hàng đã về kho</span>;
            case ORDER_STATUS.SHIPPING: return <span className="flex items-center gap-1.5 text-blue-700 bg-blue-100 px-3 py-1.5 rounded-full text-sm font-bold"><Truck size={16}/> Đang giao hàng</span>;
            case ORDER_STATUS.DELIVERED: return <span className="flex items-center gap-1.5 text-green-700 bg-green-100 px-3 py-1.5 rounded-full text-sm font-bold"><CheckCircle size={16}/> Giao thành công</span>;
            case ORDER_STATUS.CANCELLED: return <span className="flex items-center gap-1.5 text-red-700 bg-red-100 px-3 py-1.5 rounded-full text-sm font-bold"><XCircle size={16}/> Đã hủy</span>;
            case ORDER_STATUS.RETURNED: return <span className="flex items-center gap-1.5 text-orange-700 bg-orange-100 px-3 py-1.5 rounded-full text-sm font-bold"><RefreshCcw size={16}/> Đổi trả hàng</span>;
            default: return <span>Lỗi trạng thái</span>;
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-medium">Đang tải chi tiết đơn hàng...</div>;
    if (!order) return <div className="p-8 text-center text-red-500 font-medium">Không tìm thấy đơn hàng!</div>;

    const totalProductPrice = order.orderPrice || 0;
    const activeShippingFee = shippingFee !== '' ? Number(shippingFee) : (order.orderShippingFee || 0);

    const displayShippingFee = orderStatus === String(ORDER_STATUS.PENDING) ? 0 : activeShippingFee;
    const grandTotal = totalProductPrice + displayShippingFee;
    
    const paymentMethodName = order.paymentMethod?.name || order.paymentMethodName || 'Thanh toán tiền mặt (COD)';
    
    // 👉 ĐÃ SỬA: Đơn giản hóa hàm format, không thêm chữ 'Z' để chống lỗi cộng giờ
    const formatToVietnamTime = (dateString) => {
        if (!dateString) return '';
        if (dateString.includes('/')) return dateString; 
        
        const date = new Date(dateString);
        return `${date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} - ${date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
    };

    return (
        <div className="font-inter p-4 sm:p-6 lg:p-8">
            <div className="flex items-center gap-4 mb-6">
                <button onClick={() => navigate('/admin/orders')} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"><ArrowLeft size={20} className="text-gray-600" /></button>
                <div>
                    <h2 className="text-2xl font-bold text-[#002855] flex items-center gap-2">Chi tiết Đơn hàng #{order.orderId}</h2>
                    <p className="text-sm text-gray-500 mt-1 flex items-center gap-2"><Calendar size={14} /> {formatToVietnamTime(order.orderDate)}</p>
                </div>
                <div className="ml-auto">{getStatusBadge(order.orderStatus)}</div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
                            <Package className="text-tnc-orange" size={20} />
                            <h3 className="font-bold text-gray-800">Sản phẩm đã đặt</h3>
                        </div>
                        <div className="p-6 divide-y divide-gray-100">
                            {order.items && order.items.length > 0 ? order.items.map((orderItem, index) => {
                                const unitPrice = orderItem.orderItemQuantity > 0 ? (orderItem.orderItemTotalPrice / orderItem.orderItemQuantity) : 0;
                                const releasePrice = orderItem.item?.itemReleasePrice || 0; 
                                const salePrice = orderItem.item?.itemSalePrice || 0;       
                                
                                const isFlashSale = orderItem.isFlashSale;
                                const isNormalSale = !isFlashSale && salePrice > 0 && unitPrice <= salePrice;
                                
                                const productId = orderItem.item?.itemId;
                                const isItemActive = orderItem.item?.itemStatus !== 0 && orderItem.item?.itemStatus !== 5;
                                
                                const priceColorClass = isFlashSale ? 'text-tnc-orange' : (isNormalSale ? 'text-tnc-orange' : 'text-[#002855]');

                                const ImageComponent = (
                                    <>
                                        <div className={`w-20 h-20 bg-gray-50 rounded-lg border p-1 transition-colors ${isItemActive ? 'border-gray-200 group-hover:border-tnc-orange' : 'border-gray-100'}`}>
                                            <img 
                                                src={getImageUrl(orderItem.item?.itemImage || orderItem.item?.images?.[0]?.itemImageUrl)} 
                                                alt={orderItem.item?.itemName} 
                                                className={`w-full h-full object-contain mix-blend-multiply ${!isItemActive ? 'grayscale' : ''}`} 
                                                onError={(e) => { e.target.onerror = null; e.target.src = getImageUrl(null); }}
                                            />
                                        </div>
                                        {isFlashSale && (
                                            <div className="absolute -top-2 -right-2 bg-tnc-orange text-white rounded-full p-1 shadow-md">
                                                <Zap size={10} fill="currentColor"/>
                                            </div>
                                        )}
                                    </>
                                );

                                return (
                                    <div key={index} className="py-4 first:pt-0 last:pb-0 flex items-center gap-4">
                                        {isItemActive ? (
                                            <Link to={`/san-pham/${generateSlug(orderItem.item?.itemName || 'san-pham')}-id${productId}`} target="_blank" rel="noopener noreferrer" className="relative shrink-0 block group">
                                                {ImageComponent}
                                            </Link>
                                        ) : (
                                            <div className="relative shrink-0 block cursor-not-allowed opacity-60" title="Sản phẩm đã ngừng kinh doanh">
                                                {ImageComponent}
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0">
                                            {isItemActive ? (
                                                <Link to={`/san-pham/${generateSlug(orderItem.item?.itemName || 'san-pham')}-id${productId}`} target="_blank" rel="noopener noreferrer" className="font-bold text-sm md:text-base text-gray-800 hover:text-tnc-orange line-clamp-2 leading-snug transition-colors">
                                                    {orderItem.item?.itemName}
                                                </Link>
                                            ) : (
                                                <span className="font-bold text-gray-400 text-sm md:text-base line-clamp-2 leading-tight mb-1" title="Sản phẩm đã ngừng kinh doanh">
                                                    {orderItem.item?.itemName} (Ngừng kinh doanh)
                                                </span>
                                            )}

                                            <div className="text-xs text-gray-500 mt-1">
                                                Mã SP: <span className="font-mono text-blue-600 font-bold">{orderItem.item?.itemCode || productId}</span>
                                            </div>
                                            
                                            <div className="flex items-center gap-2 mt-1">
                                                <p className="text-sm font-bold text-gray-600">SL: {orderItem.orderItemQuantity}</p>
                                                {isFlashSale ? (
                                                    <span className="text-[10px] text-tnc-orange font-bold bg-orange-100 px-1.5 py-0.5 rounded">Flash Sale</span>
                                                ) : (isNormalSale && (
                                                    <span className="text-[10px] text-orange-600 font-bold bg-orange-100 px-1.5 py-0.5 rounded">Giảm giá</span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="text-right shrink-0 flex flex-col items-end">
                                            <span className={`font-bold block text-lg ${priceColorClass}`}>
                                                {formatPrice(orderItem.orderItemTotalPrice)}
                                            </span>
                                            
                                            <div className="flex flex-col items-end mt-0.5 space-y-0.5">
                                                {isFlashSale && salePrice > 0 && salePrice > unitPrice && (
                                                    <span className="text-xs text-tnc-orange line-through block" title="Giá giảm thông thường">
                                                        {formatPrice(salePrice * orderItem.orderItemQuantity)}
                                                    </span>
                                                )}

                                                {releasePrice > 0 && releasePrice > unitPrice && releasePrice !== salePrice && (
                                                    <span className="text-[11px] text-gray-400 line-through block" title="Giá gốc của sản phẩm">
                                                        {formatPrice(releasePrice * orderItem.orderItemQuantity)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }) : <p className="text-center py-4 text-gray-500">Không có dữ liệu sản phẩm.</p>}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CreditCard className="text-tnc-orange" size={20} />
                                <h3 className="font-bold text-gray-800">Thanh toán</h3>
                            </div>
                            {order.isPaid ? (
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold flex items-center gap-1 shadow-sm"><CheckCircle2 size={14}/> Đã trả</span>
                            ) : (
                                <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-bold flex items-center gap-1 shadow-sm"><Clock size={14}/> Chưa trả</span>
                            )}
                        </div>
                        <div className="p-6 space-y-3">
                            <div className="flex justify-between items-start text-sm mb-3 border-b border-gray-100 pb-3">
                                <span className="text-gray-500 mt-0.5">Phương thức:</span>
                                <span className="font-bold text-[#002855] text-right w-2/3 leading-snug">{paymentMethodName}</span>
                            </div>

                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-500">Tạm tính ({order.items?.length || 0} món)</span>
                                <span className="font-medium text-gray-800">{formatPrice(totalProductPrice)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm pb-3 border-b border-gray-100">
                                <span className="text-gray-500">Phí vận chuyển</span>
                                <span className="font-medium text-gray-800">
                                    {orderStatus === String(ORDER_STATUS.PENDING) 
                                        ? <span className="italic text-orange-500 font-bold">Đang chờ xác nhận</span> 
                                        : formatPrice(activeShippingFee)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center pt-1">
                                <span className="font-bold text-gray-800">Tổng cộng</span>
                                <span className="font-bold text-2xl text-tnc-orange">{formatPrice(grandTotal)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50"><h3 className="font-bold text-gray-800">Ghi chú của khách</h3></div>
                        <div className="p-6 text-sm text-gray-600">
                            {order.orderNote ? <p className="italic bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-yellow-800 whitespace-pre-wrap">"{order.orderNote}"</p> : <p className="text-gray-400">Không có ghi chú nào.</p>}
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border-2 border-blue-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-blue-100 bg-blue-50 flex items-center gap-2">
                            <CheckSquare className="text-blue-600" size={20} />
                            <h3 className="font-bold text-[#002855]">Xử lý đơn hàng</h3>
                        </div>
                        <div className="p-6 space-y-5">
                            
                            <div>
                                <label className="flex items-center justify-between text-sm font-bold text-gray-700 mb-2">
                                    <span>Phí giao hàng (VNĐ):</span>
                                    {isShippingFeeChanged && orderStatus !== String(ORDER_STATUS.PENDING) && (
                                        <span className="text-[11px] text-orange-600 italic font-medium flex items-center gap-1 animate-pulse">
                                            <AlertTriangle size={12}/> Có thay đổi
                                        </span>
                                    )}
                                </label>
                                
                                {orderStatus === String(ORDER_STATUS.PENDING) ? (
                                    <div className="space-y-2">
                                        <input 
                                            type="text"
                                            value="Đang chờ xác nhận"
                                            disabled
                                            className="w-full px-4 py-2.5 text-gray-500 bg-gray-100 rounded-lg border-2 border-gray-200 outline-none font-bold shadow-sm cursor-not-allowed text-center"
                                        />
                                        <p className="text-[11px] text-orange-600 font-medium flex items-center gap-1">
                                            <AlertTriangle size={13} className="shrink-0"/> Ghi chú: Chuyển trạng thái khác "Chờ xác nhận" để có thể nhập phí ship.
                                        </p>
                                    </div>
                                ) : (
                                    <input 
                                        type="number"
                                        value={shippingFee}
                                        onChange={(e) => setShippingFee(e.target.value)}
                                        placeholder="Nhập phí (VD: 30000)"
                                        className={`w-full px-4 py-2.5 text-gray-900 rounded-lg border-2 outline-none transition-all font-bold shadow-sm ${isShippingFeeChanged ? 'border-orange-400 border-dashed bg-orange-50/30' : 'border-gray-200 focus:border-tnc-orange bg-white'}`}
                                    />
                                )}
                            </div>

                            <div>
                                <label className="flex items-center justify-between text-sm font-bold text-gray-700 mb-2">
                                    <span>Trạng thái Thanh toán:</span>
                                    {isPaymentChanged && <span className="text-[11px] text-orange-600 italic font-medium flex items-center gap-1 animate-pulse"><AlertTriangle size={12}/> Có thay đổi</span>}
                                </label>
                                <select 
                                    value={paymentStatus} 
                                    onChange={(e) => setPaymentStatus(e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-lg border-2 outline-none transition-all font-bold cursor-pointer appearance-none shadow-sm
                                        ${isPaymentChanged ? 'border-orange-400 border-dashed' : 'border-gray-200 focus:border-tnc-orange'}
                                        ${paymentStatus === 'true' ? 'text-green-700 bg-green-50' : 'text-orange-700 bg-orange-50'}`}
                                >
                                    <option value="false">🕒 Chưa thanh toán</option>
                                    <option value="true">✅ Đã thanh toán</option>
                                </select>
                            </div>

                            <div>
                                <label className="flex items-center justify-between text-sm font-bold text-gray-700 mb-2">
                                    <span>Trạng thái Đơn hàng:</span>
                                    {isStatusChanged && <span className="text-[11px] text-orange-600 italic font-medium flex items-center gap-1 animate-pulse"><AlertTriangle size={12}/> Có thay đổi</span>}
                                </label>
                                <select 
                                    value={orderStatus} 
                                    onChange={(e) => setOrderStatus(e.target.value)}
                                    className={`w-full px-4 py-2.5 text-gray-900 rounded-lg border-2 outline-none transition-all font-bold cursor-pointer appearance-none shadow-sm ${isStatusChanged ? 'border-orange-400 border-dashed bg-orange-50/30' : 'border-gray-200 focus:border-tnc-orange bg-white'}`}
                                >
                                    <option value={ORDER_STATUS.PENDING}>0 - Chờ xác nhận</option>
                                    <option value={ORDER_STATUS.CONFIRMED}>1 - Đã xác nhận</option>
                                    <option value={ORDER_STATUS.IN_WAREHOUSE}>2 - Hàng đã về kho</option> 
                                    <option value={ORDER_STATUS.SHIPPING}>3 - Đang giao hàng</option> 
                                    <option value={ORDER_STATUS.DELIVERED}>4 - Giao thành công</option> 
                                    <option value={ORDER_STATUS.CANCELLED}>5 - Hủy đơn</option> 
                                    <option value={ORDER_STATUS.RETURNED}>6 - Đổi trả hàng</option> 
                                </select>
                            </div>

                            <div className="pt-2">
                                <button 
                                    onClick={handleUpdateOrder}
                                    disabled={isSaving || !hasUnsavedChanges}
                                    className={`w-full text-white font-bold py-3.5 rounded-lg transition-colors flex justify-center items-center gap-2 shadow-md
                                        ${!hasUnsavedChanges ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#002855] hover:bg-blue-900'}`}
                                >
                                    {isSaving ? <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span> : <Save size={18}/>} 
                                    {isSaving ? "ĐANG LƯU..." : "LƯU THAY ĐỔI"}
                                </button>

                                {/* NÚT GỬI EMAIL THÔNG MINH */}
                                {(() => {
                                    const isEmailSentForCurrentStatus = order.lastEmailSentStatus === order.orderStatus;
                                    
                                    return (
                                        <div className="mt-4 border-t border-gray-100 pt-4">
                                            <button 
                                                onClick={handleSendStatusEmail}
                                                disabled={isSendingEmail || hasUnsavedChanges}
                                                className={`w-full font-bold py-3.5 rounded-lg border-2 transition-all flex justify-center items-center gap-2
                                                    ${hasUnsavedChanges || isSendingEmail
                                                        ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed' 
                                                        : isEmailSentForCurrentStatus
                                                            ? 'border-green-600 text-green-700 bg-green-50 hover:bg-green-100' 
                                                            : 'border-[#002855] text-[#002855] hover:bg-[#002855] hover:text-white' 
                                                    }`}
                                            >
                                                {isSendingEmail ? (
                                                    <span className={`animate-spin h-5 w-5 border-2 border-t-transparent rounded-full ${hasUnsavedChanges ? 'border-gray-400' : 'border-[#002855]'}`}></span>
                                                ) : isEmailSentForCurrentStatus ? (
                                                    <CheckCircle2 size={18}/>
                                                ) : (
                                                    <Send size={18}/>
                                                )} 
                                                
                                                {isSendingEmail 
                                                    ? "ĐANG GỬI EMAIL..." 
                                                    : isEmailSentForCurrentStatus 
                                                        ? "GỬI LẠI EMAIL (ĐÃ GỬI)" 
                                                        : "GỬI EMAIL THÔNG BÁO"}
                                            </button>

                                            {/* HIỂN THỊ LOG GHI CHÚ */}
                                            {hasUnsavedChanges ? (
                                                <p className="text-center text-[11px] text-orange-500 font-medium mt-2 flex items-center justify-center gap-1">
                                                    <AlertTriangle size={12}/> Vui lòng lưu thay đổi trước khi gửi email cho khách
                                                </p>
                                            ) : order.lastEmailSentTime && (
                                                <p className="text-center text-xs text-gray-500 mt-2">
                                                    Gửi lần cuối: <span className="font-bold">{formatToVietnamTime(order.lastEmailSentTime)}</span>
                                                </p>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* THÔNG TIN GIAO HÀNG CHO PHÉP CHỈNH SỬA */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <User className="text-tnc-orange" size={20} />
                                <h3 className="font-bold text-gray-800">Thông tin Giao hàng</h3>
                            </div>
                            {isCustomerInfoChanged && <span className="text-[11px] text-orange-600 italic font-medium flex items-center gap-1 animate-pulse"><AlertTriangle size={12}/> Có thay đổi</span>}
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="text-xs text-gray-500 font-medium mb-1 block">Tài khoản đặt hàng</label>
                                <p className="font-bold text-blue-600">
                                    {(order && order.account && order.account.username) ? `@${order.account.username}` : 'Khách vãng lai'}
                                </p>
                            </div>

                            <div>
                                <label className="text-xs text-gray-500 font-medium mb-1 block">Người nhận</label>
                                <input 
                                    type="text"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    className={`w-full px-3 py-2 border-2 rounded-lg outline-none font-bold text-gray-800 transition-colors ${customerName !== (order?.orderCustomerName || '') ? 'border-orange-400 border-dashed bg-orange-50/30' : 'border-gray-200 focus:border-tnc-orange'}`}
                                />
                            </div>

                            <div>
                                <label className="text-xs text-gray-500 font-medium mb-1 block">Điện thoại</label>
                                <input 
                                    type="text"
                                    value={customerPhone}
                                    onChange={(e) => setCustomerPhone(e.target.value)}
                                    className={`w-full px-3 py-2 border-2 rounded-lg outline-none font-medium transition-colors ${customerPhone !== (order?.orderPhone || '') ? 'border-orange-400 border-dashed bg-orange-50/30' : 'border-gray-200 focus:border-tnc-orange'}`}
                                />
                            </div>
                            
                            <div>
                                <label className="text-xs text-gray-500 font-medium mb-1 flex items-center gap-1">
                                    <Mail size={12}/> Email liên hệ
                                </label>
                                <input 
                                    type="email"
                                    value={customerEmail}
                                    onChange={(e) => setCustomerEmail(e.target.value)}
                                    placeholder="Chưa có email"
                                    className={`w-full px-3 py-2 border-2 rounded-lg outline-none font-medium transition-colors ${customerEmail !== (order?.orderEmail || '') ? 'border-orange-400 border-dashed bg-orange-50/30' : 'border-gray-200 focus:border-tnc-orange'}`}
                                />
                            </div>
                            
                            <div>
                                <label className="text-xs text-gray-500 font-medium mb-1 block">Địa chỉ nhận hàng</label>
                                <textarea 
                                    rows="3"
                                    value={customerAddress}
                                    onChange={(e) => setCustomerAddress(e.target.value)}
                                    className={`w-full px-3 py-2 border-2 rounded-lg outline-none font-medium resize-none transition-colors ${customerAddress !== (order?.orderAddress || '') ? 'border-orange-400 border-dashed bg-orange-50/30' : 'border-gray-200 focus:border-tnc-orange'}`}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AdminOrderDetail;